/* =========================================================================
 * IBC — delivery prices, asked of the checkout rather than worked out here
 *
 * Paints the standard delivery price into every [data-ibc-delivery-quote]
 * on the page:
 *
 *   data-ibc-delivery-quote="basket"   what delivering the basket costs
 *   data-ibc-delivery-quote="item"     what delivering this item on its own
 *                                      costs; carries data-variant-id
 *
 * WHERE THE NUMBER COMES FROM. Not from a copy of the weight bands. Shopify
 * prices delivery from Settings > Shipping and delivery, and Liquid cannot
 * read those rates. A table typed into the theme would be a second copy that
 * drifts the first time a band is repriced, so instead the Storefront API is
 * asked to build a throwaway cart holding the same lines and to say what its
 * delivery options cost. That is the engine checkout uses, so the figure
 * carries every band, the packaging weight and any rate change made in the
 * admin, with nothing in the theme to update.
 *
 * The cart is a SEPARATE one. `cartCreate` never touches the customer's
 * basket cookie, which is what lets the product page price an item that is
 * not in the basket. Tokenless Storefront access covers cart operations, so
 * no access token is embedded in the page.
 *
 * WHICH OPTION is the one whose title matches the shop's standard rate name
 * (theme setting `delivery_rate_name`, carried on the element). Store pickup
 * is a £0 option in the same list, so taking the cheapest would be wrong.
 * A basket split into several delivery groups is charged per group, so the
 * figure is the sum; if any group cannot take the named rate the basket
 * cannot be delivered that way at all, and the slot stays hidden.
 *
 * NOTHING SHOWS UNTIL A REAL NUMBER ARRIVES. Every slot is rendered `hidden`
 * and only unhidden with a price in it. A failed request, an empty basket, a
 * pickup-only product or an overweight basket all leave it hidden: a wrong
 * delivery price is worse than none, and checkout still prices it properly.
 *
 * SURVIVING RE-RENDERS. assets/ibc-cart.js swaps the basket sections' HTML
 * after every change, which throws away anything painted into them. A
 * MutationObserver notices the fresh, unpainted slots and paints them from
 * the cached quote, so a re-render costs no network request unless the
 * basket's contents actually changed.
 *
 * Loaded by sections/ibc-cart-drawer.liquid (every page) and
 * sections/main-cart.liquid, so it can run twice on /cart: re-entry guard.
 * ====================================================================== */
(function () {
  'use strict';

  if (window.IbcDeliveryQuote) return;
  window.IbcDeliveryQuote = true;

  /* The Storefront API version. Any supported version works; this is the
   * one the quote was verified against. */
  var API = '/api/2026-07/graphql.json';
  var SELECTOR = '[data-ibc-delivery-quote]';

  var MUTATION =
    'mutation IbcDeliveryQuote($lines: [CartLineInput!], $country: CountryCode!, $countryName: String!) {' +
    '  cartCreate(input: {' +
    '    lines: $lines,' +
    '    buyerIdentity: { countryCode: $country, deliveryAddressPreferences: [{ deliveryAddress: { country: $countryName } }] }' +
    '  }) {' +
    '    cart { deliveryGroups(first: 10) { nodes { deliveryOptions { title estimatedCost { amount currencyCode } } } } }' +
    '    userErrors { message }' +
    '  }' +
    '}';

  /* signature -> Promise<{ amount, currency } | null> */
  var cache = {};

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function signature(lines, country, rateName) {
    return (
      country + '|' + rateName + '|' +
      lines
        .map(function (line) { return line.id + 'x' + line.quantity; })
        .sort()
        .join(',')
    );
  }

  function quote(lines, country, rateName) {
    if (!lines.length) return Promise.resolve(null);
    var key = signature(lines, country, rateName);
    if (cache[key]) return cache[key];

    cache[key] = fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query: MUTATION,
        variables: {
          country: country,
          countryName: country,
          lines: lines.map(function (line) {
            return {
              merchandiseId: 'gid://shopify/ProductVariant/' + line.id,
              quantity: line.quantity
            };
          })
        }
      })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('quote ' + response.status);
        return response.json();
      })
      .then(function (json) {
        var result = json && json.data && json.data.cartCreate;
        var groups = result && result.cart && result.cart.deliveryGroups
          ? result.cart.deliveryGroups.nodes
          : [];
        if (!groups.length) return null;

        var wanted = rateName.trim().toLowerCase();
        var total = 0;
        var currency = '';
        for (var i = 0; i < groups.length; i++) {
          var match = null;
          var options = groups[i].deliveryOptions || [];
          for (var j = 0; j < options.length; j++) {
            if ((options[j].title || '').trim().toLowerCase() === wanted) {
              match = options[j];
              break;
            }
          }
          if (!match || !match.estimatedCost) return null;
          total += parseFloat(match.estimatedCost.amount);
          currency = match.estimatedCost.currencyCode;
        }
        return isNaN(total) ? null : { amount: total, currency: currency };
      })
      .catch(function () {
        /* Forget a failure, so the next change can try again. */
        delete cache[key];
        return null;
      });

    return cache[key];
  }

  function format(price) {
    if (price.amount === 0) return 'Free';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: price.currency
      }).format(price.amount);
    } catch (error) {
      return price.amount.toFixed(2);
    }
  }

  function paint(element, price, key) {
    element.setAttribute('data-quoted', key);
    if (!price) {
      element.hidden = true;
      element.textContent = '';
    } else {
      /* `data-prefix` lets a slot qualify the figure -- "from £3.95" on the
         Delivery tab, where same-day sits behind the same tab at a higher
         price. A free rate is printed as "Free" with no prefix. */
      var prefix = price.amount === 0 ? '' : element.getAttribute('data-prefix') || '';
      element.textContent = prefix + format(price);
      element.hidden = false;
    }
    syncNotes();
  }

  /* A note explains the prices beside it -- the ones sharing its parent -- and
   * is shown only while one of them has a figure on screen. Scoped that
   * narrowly so a quick-view panel's note cannot be lit by the page's. */
  function syncNotes() {
    toArray(document.querySelectorAll('[data-ibc-delivery-quote-note]')).forEach(function (note) {
      var scope = note.parentElement || document;
      var shown = toArray(scope.querySelectorAll(SELECTOR)).some(function (slot) {
        return !slot.hidden;
      });
      note.hidden = !shown;
    });
  }

  function settingsOf(element) {
    return {
      country: element.getAttribute('data-country') || 'GB',
      rateName: element.getAttribute('data-rate-name') || 'Delivery'
    };
  }

  /* ------------------------------------------------------------ basket -- */

  function basketLines() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        return (cart.items || []).map(function (item) {
          return { id: item.variant_id, quantity: item.quantity };
        });
      });
  }

  function updateBasket(slots) {
    if (!slots.length) return;
    basketLines()
      .then(function (lines) {
        var opts = settingsOf(slots[0]);
        var key = signature(lines, opts.country, opts.rateName);
        return quote(lines, opts.country, opts.rateName).then(function (price) {
          slots.forEach(function (slot) { paint(slot, price, key); });
        });
      })
      .catch(function () {
        slots.forEach(function (slot) { slot.hidden = true; });
      });
  }

  /* -------------------------------------------------------------- item -- */

  function quantityFor(slot) {
    var owner = slot.closest('ibc-product') || slot.closest('form') || document;
    var input = owner.querySelector('input[name="quantity"]');
    var value = input ? parseInt(input.value, 10) : 1;
    return isNaN(value) || value < 1 ? 1 : value;
  }

  function updateItems(slots) {
    slots.forEach(function (slot) {
      var id = slot.getAttribute('data-variant-id');
      if (!id) return;
      var lines = [{ id: Number(id), quantity: quantityFor(slot) }];
      var opts = settingsOf(slot);
      var key = signature(lines, opts.country, opts.rateName);
      if (slot.getAttribute('data-quoted') === key) return;
      quote(lines, opts.country, opts.rateName).then(function (price) {
        paint(slot, price, key);
      });
    });
  }

  /* ---------------------------------------------------------- schedule -- */

  var timer = null;
  var basketDirty = true;

  function schedule(basketChanged) {
    if (basketChanged) basketDirty = true;
    clearTimeout(timer);
    timer = setTimeout(run, 150);
  }

  function run() {
    var slots = toArray(document.querySelectorAll(SELECTOR));
    var basket = slots.filter(function (slot) {
      return slot.getAttribute('data-ibc-delivery-quote') === 'basket';
    });
    var items = slots.filter(function (slot) {
      return slot.getAttribute('data-ibc-delivery-quote') === 'item';
    });

    /* A basket that has not changed is painted from the cache by the same
     * call: quote() returns the stored promise for an unchanged signature,
     * and the only network cost is the /cart.js read that proves it. */
    var unpainted = basket.some(function (slot) { return !slot.hasAttribute('data-quoted'); });
    if (basket.length && (basketDirty || unpainted)) {
      basketDirty = false;
      updateBasket(basket);
    }
    updateItems(items);
  }

  /* The basket changed. Every controller that mutates it announces this. */
  document.addEventListener('ibc:cart-changed', function () { schedule(true); });
  window.addEventListener('ibc:cart-changed', function () { schedule(true); });

  /* A different variant was chosen on a product page. */
  document.addEventListener('ibc:product-selection', function (event) {
    var detail = event.detail || {};
    if (!detail.variantId || !detail.element) return;
    toArray(detail.element.querySelectorAll('[data-ibc-delivery-quote="item"]')).forEach(function (slot) {
      slot.setAttribute('data-variant-id', String(detail.variantId));
    });
    schedule(false);
  });

  /* The quantity stepper dispatches `change` on the input itself. */
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (target && target.name === 'quantity') schedule(false);
  });

  /* Sections re-rendered from the server arrive with fresh, unpainted slots. */
  function watch() {
    if (!window.MutationObserver) return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches(SELECTOR) || node.querySelector(SELECTOR)) {
            schedule(false);
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    watch();
    schedule(true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
